import React, { useState, useEffect } from 'react';
import api from '../services/api';
import useAuthStore from '../store/authStore';

import {
  IconeHorloge, IconeCrayon, IconeValide,
} from './IconesSVG';

// Formulaire dynamique pour generer un memo PDF a partir du gabarit configure en base.
// type_memo: 'filiale' ou 'holding'
// filialeId: id de la filiale (utilise pour charger le bon gabarit type 'filiale')
// pochetteId: id de la pochette dans laquelle le PDF genere sera ajoute
// valeursInitiales: valeurs precedemment saisies, pour pre-remplir (cas "Changer le memo")
// demande: la pochette d'origine, dont on reprend le montant
// onGenere: callback appele apres succes, avec (document, numero_memo, valeursUtilisees)
export default function FormulaireMemo({ type_memo, filialeId, pochetteId, onGenere, onAnnuler, valeursInitiales, demande }) {
  const { user } = useAuthStore();
  const [modele, setModele] = useState(null);
  const [numeroMemo, setNumeroMemo] = useState(null);
  const [valeurs, setValeurs] = useState({});
  const [chargement, setChargement] = useState(true);
  const [generation, setGeneration] = useState(false);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    initialiser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type_memo, filialeId, pochetteId]);

  // Le champ « formule » a ete retire du memo filiale. Le gabarit vit en base et
  // peut encore le contenir : on le filtre a l'affichage, sur le code comme sur
  // le libelle, plutot que de dependre d'une migration de donnees.
  const estFormule = (champ) => {
    const normaliser = (t) => String(t || '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    return normaliser(champ.code).includes('formule') || normaliser(champ.label).includes('formule');
  };

  const champsVisibles = (data) => (data?.champs || []).filter(c => !estFormule(c));

  // Le mode de paiement n'est pas toujours connu au moment de preparer le memo :
  // il reste affiche, mais ne bloque plus la generation. Comme pour « formule »,
  // le gabarit vit en base et le declare encore obligatoire ; on neutralise
  // l'exigence ici plutot que d'attendre une migration de donnees.
  const CODES_FACULTATIFS = ['mode_paiement'];
  const estObligatoire = (champ) => champ.obligatoire && !CODES_FACULTATIFS.includes(champ.code);

  // Valeurs reprises du contexte, pour eviter une double saisie de ce que
  // l'application connait deja. L'utilisateur peut les corriger.
  const prerempli = (code) => {
    if (code === 'montant') {
      // `montant` est un DECIMAL avec 0 par defaut, que Sequelize renvoie en
      // chaine : un simple `||` retiendrait la chaine "0", pourtant truthy, et
      // pre-remplirait le memo avec un montant nul. On ne retient qu'une valeur
      // reellement renseignee, sinon on se rabat sur le montant estime.
      const utilisable = (v) => v !== undefined && v !== null && v !== '' && Number(v) > 0;
      const montant = utilisable(demande?.montant) ? demande.montant
        : utilisable(demande?.montantEstime) ? demande.montantEstime : null;
      return montant === null ? '' : String(montant);
    }
    if (code === 'prepare_par') {
      return [user?.prenom, user?.nom].filter(Boolean).join(' ');
    }
    return '';
  };

  const initialiser = async () => {
    setChargement(true);
    setErreur('');
    try {
      // 1. Charger le gabarit (liste des champs a afficher)
      const params = type_memo === 'filiale' && filialeId ? '?filiale_id=' + filialeId : '';
      const resModele = await api.get('/demandes/modele-memo/' + type_memo + params);
      const data = resModele.data;
      setModele(data);

      const init = {};
      champsVisibles(data).forEach(c => {
        // Priorite : ce qui a deja ete saisi (cas « Changer le memo »), puis le
        // pre-remplissage, puis vide. Les valeurs pre-remplies restent editables.
        const dejaSaisi = valeursInitiales && valeursInitiales[c.code];
        init[c.code] = dejaSaisi || prerempli(c.code) || '';
      });
      setValeurs(init);

      // 2. Reserver immediatement un numero de memo unique (lecture seule, non modifiable)
      const resNumero = await api.post('/demandes/' + pochetteId + '/reserver-numero-memo', { type_memo });
      setNumeroMemo(resNumero.numero_memo);
    } catch (err) {
      setErreur(err.response?.data?.message || 'Aucun gabarit de memo configure pour ce type');
    } finally {
      setChargement(false);
    }
  };

  const champsManquants = () => {
    if (!modele) return [];
    return champsVisibles(modele)
      .filter(c => estObligatoire(c) && !String(valeurs[c.code] || '').trim())
      .map(c => c.label);
  };

  const genererMemo = async () => {
    const manquants = champsManquants();
    if (manquants.length > 0) {
      setErreur('Champs obligatoires manquants : ' + manquants.join(', '));
      return;
    }
    setGeneration(true);
    setErreur('');
    try {
      const res = await api.post('/demandes/' + pochetteId + '/generer-memo', {
        type_memo,
        donnees: valeurs,
        numero_memo: numeroMemo,
      });
      onGenere && onGenere(res.data, res.numero_memo, valeurs);
    } catch (err) {
      setErreur(err.response?.data?.message || 'Erreur lors de la generation du memo');
    } finally {
      setGeneration(false);
    }
  };

  if (chargement) {
    return (
      <div style={s.boite}>
        <div style={{ textAlign: 'center', padding: 20, color: '#888', fontSize: 12 }}>
          <IconeHorloge taille={14} /> Chargement du gabarit...
        </div>
      </div>
    );
  }

  if (!modele) {
    return (
      <div style={s.boite}>
        <div style={s.erreur}>
          {erreur || 'Aucun gabarit configure pour ce type de memo'}
        </div>
        {onAnnuler && (
          <button type="button" onClick={onAnnuler} style={s.btnAnnuler}>Fermer</button>
        )}
      </div>
    );
  }

  return (
    <div style={s.boite}>
      <div style={s.titre}>
        <IconeCrayon taille={14} /> Generer le {type_memo === 'filiale' ? 'Memo Filiale' : 'Memo Holding'}
      </div>

      {numeroMemo && (
        <div style={s.numeroBox}>
          <span style={{ color: '#888' }}>N° de memo (genere automatiquement) :</span>{' '}
          <strong>{numeroMemo}</strong>
        </div>
      )}

      <div style={s.sousTitre}>
        Remplissez les champs ci-dessous, le PDF sera genere automatiquement.
      </div>

      <div style={s.champs}>
        {champsVisibles(modele).map(champ => (
          <div key={champ.code} style={s.groupeChamp}>
            <label style={s.label}>
              {champ.label}
              {estObligatoire(champ) && <span style={{ color: '#C0392B' }}> *</span>}
            </label>

            {champ.type === 'texte_long' ? (
              <textarea
                value={valeurs[champ.code] || ''}
                onChange={e => setValeurs(v => ({ ...v, [champ.code]: e.target.value }))}
                style={s.textarea}
                rows={4}
              />
            ) : champ.type === 'select' ? (
              <select
                value={valeurs[champ.code] || ''}
                onChange={e => setValeurs(v => ({ ...v, [champ.code]: e.target.value }))}
                style={s.input}
              >
                <option value="">-- Choisir --</option>
                {(champ.options || []).map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            ) : (
              <input
                type={champ.type === 'nombre' ? 'number' : 'text'}
                value={valeurs[champ.code] || ''}
                onChange={e => setValeurs(v => ({ ...v, [champ.code]: e.target.value }))}
                style={s.input}
              />
            )}
          </div>
        ))}
      </div>

      {erreur && <div style={s.erreur}>{erreur}</div>}

      <div style={s.actions}>
        {onAnnuler && (
          <button type="button" onClick={onAnnuler} style={s.btnAnnuler} disabled={generation}>
            Annuler
          </button>
        )}
        <button type="button" onClick={genererMemo} style={s.btnGenerer} disabled={generation}>
          {generation ? <><IconeHorloge taille={14} /> Generation...</> : <><IconeValide taille={14} /> Generer le PDF</>}
        </button>
      </div>
    </div>
  );
}

const s = {
  boite: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, fontFamily: 'inherit' },
  titre: { fontSize: 14, fontWeight: 700, color: '#0D1B2A', marginBottom: 8 },
  numeroBox: { background: '#F4F6F7', border: '1px solid #D5D8DC', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#333', marginBottom: 10 },
  sousTitre: { fontSize: 12, color: '#888', marginBottom: 14 },
  champs: { display: 'flex', flexDirection: 'column', gap: 12 },
  groupeChamp: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: 12, fontWeight: 600, color: '#333' },
  input: { padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, fontFamily: 'inherit', outline: 'none' },
  textarea: { padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, fontFamily: 'inherit', outline: 'none', resize: 'vertical' },
  erreur: { background: '#FDEDEC', color: '#96281B', padding: '10px 12px', borderRadius: 8, fontSize: 12, marginTop: 12 },
  actions: { display: 'flex', gap: 8, marginTop: 16 },
  btnAnnuler: { flex: 1, padding: '10px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', color: '#555' },
  btnGenerer: { flex: 2, padding: '10px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #27AE60, #1E8449)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
};
